import {
  useAnalytics,
  useMetrics,
  useObservabilityConfig,
  useProject,
  useReports,
} from '@sail/observability';
import {useCallback} from 'react';
import logHttp from 'src/internal/logHttp';

import type {ErrorSource} from 'src/internal/telemetry/types';

type ErrorSettings = {
  expectedErrors?: ReadonlyArray<number>;
  mutedErrors?: ReadonlyArray<number>;
};

type ObservabilityConfig = {
  expectedApiErrors: ReadonlyArray<string | number>;
  mutedApiErrors: ReadonlyArray<string | number>;
};

export default function useLogFetch(
  observabilityConfig: ObservabilityConfig,
): (res: Response, duration: number, settings: ErrorSettings) => void {
  const project = useProject();
  const service = useObservabilityConfig()?.service;
  const errors = useReports();
  const metrics = useMetrics();
  const analytics = useAnalytics();
  const {expectedApiErrors, mutedApiErrors} = observabilityConfig;

  return useCallback(
    (res: Response, duration: number, settings: ErrorSettings) => {
      const {headers, status, statusText, url} = res;
      const {expectedErrors, mutedErrors} = settings;

      const computedExpectedErrors = expectedApiErrors.concat(
        expectedErrors ?? [],
      );

      const computedMutedErrors = mutedApiErrors.concat(mutedErrors ?? []);

      if (computedExpectedErrors.includes(status)) {
        return;
      }

      let errorSource: ErrorSource = '<unknown>';

      if (status < 100) {
        errorSource = 'network';
      }

      if (status >= 400 && status < 500) {
        errorSource = 'client';
      }

      if (status >= 500 && status < 600) {
        errorSource = 'server';
      }

      const observabilityFns = {errors, metrics, analytics};

      const mute = !!computedMutedErrors.includes(status);

      logHttp('Fetch', observabilityFns, {
        // Ownership information:
        service: service ?? '<unknown>',
        project: project ?? '<unknown>',
        component: '<unknown>',

        // Error information:
        errorCode: `HTTP ${status}`,
        errorMessage: statusText,
        errorPath: '',
        errorExpected: false,
        errorMuted: mute,
        errorSource,

        // Operation information:
        operationTag: 'http',
        operationName: url,
        operationDeduplicate: false,
        operationPrefetch: false,
        operationPrefetchHit: false,

        // Network information:
        networkDuration: duration ?? null,
        networkStatus: status ?? null,
        networkActionId: null,
        networkRequestId: headers.get('Request-Id') ?? null,
        networkTraceId: null,
      });
    },
    [
      expectedApiErrors,
      mutedApiErrors,
      errors,
      metrics,
      analytics,
      service,
      project,
    ],
  );
}
